import { API_URL } from '../config';

async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const text = await res.text();
    if (!text?.trim()) return fallback;
    try {
      const j = JSON.parse(text) as {
        message?: string;
        title?: string;
        errors?: Record<string, string[]>;
      };
      if (j.message) return j.message;
      if (j.title) return j.title;
      if (j.errors && typeof j.errors === 'object') {
        const first = Object.values(j.errors).flat()[0];
        if (first) return String(first);
      }
      return text;
    } catch {
      return text;
    }
  } catch {
    /* ignore */
  }
  return fallback;
}

export interface TipoElemento {
  id: number;
  nombre: string;
  activo: boolean;
  detalles?: TipoElementoDetalle[];
  fechaCreacion?: string;
  fechaUpdate?: string;
  usuarioCreacion?: string;
  usuarioUpdate?: string;
}

export interface TipoElementoDetalle {
  id: number;
  item: string;
  /** FK en formulario / UI (mismo valor que tipoDatoId del API). */
  tipoDeDatoId: number;
  /** Como viene en GET /api/TiposDeElemento/{id} (camelCase). */
  tipoDatoId?: number;
  tipoDeDato?: TipoDato;
  tipoDeElementoId: number;
  /** Como serializa .NET el FK (`TipoElementoId`). */
  tipoElementoId?: number;
  requerido?: boolean;
  activo?: boolean;
  fechaCreacion?: string;
  fechaUpdate?: string;
  usuarioCreacion?: string;
  usuarioUpdate?: string;
}

export interface TipoDato {
  id: number;
  nombre: string;
  fechaCreacion?: string;
  fechaUpdate?: string;
  usuarioCreacion?: string;
  usuarioUpdate?: string;
}

export async function getTiposElemento(): Promise<TipoElemento[]> {
  const res = await fetch(`${API_URL}/api/TiposDeElemento`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
    },
  });
  if (!res.ok) throw new Error('Error al obtener tipos de elemento');
  return res.json();
}

export async function getTipoElemento(id: number): Promise<TipoElemento> {
  const res = await fetch(`${API_URL}/api/TiposDeElemento/${id}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
    },
  });
  if (!res.ok) throw new Error('Error al obtener tipo de elemento');
  return res.json();
}

export async function createTipoElemento(
  tipoElemento: Omit<TipoElemento, 'id'>,
): Promise<TipoElemento> {
  const res = await fetch(`${API_URL}/api/TiposDeElemento`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(tipoElemento),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, 'Error al crear tipo de elemento'));
  }
  return res.json();
}

export async function updateTipoElemento(
  id: number,
  tipoElemento: Record<string, unknown>,
): Promise<void> {
  const res = await fetch(`${API_URL}/api/TiposDeElemento/${id}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(tipoElemento),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, 'Error al actualizar tipo de elemento'));
  }
}

export async function deleteTipoElemento(id: number): Promise<void> {
  const res = await fetch(`${API_URL}/api/TiposDeElemento/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
    },
  });
  if (!res.ok) throw new Error('Error al eliminar tipo de elemento');
}

export async function getTiposDato(): Promise<TipoDato[]> {
  const res = await fetch(`${API_URL}/api/TiposDeDato`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
    },
  });
  if (!res.ok) throw new Error('Error al obtener tipos de dato');
  return res.json();
}
